import React from 'react'

import { SubscriptionPaymentType } from '@thg-commerce/enterprise-components/ProductVariations'
import {
  Option,
  VariantFields as TransformerVariant,
} from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import { i18n, useSiteConfig } from '@thg-commerce/enterprise-core'
import {
  ProductPageData,
  SelectedOptions,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  ChoiceMap,
  VariantChoices,
} from '@thg-commerce/enterprise-network/src/transformers/product/options'
import { TransformedOption as AuroraOption } from '@thg-commerce/enterprise-product-options/src/transformers/option'
import { ProductOptionsMap } from '@thg-commerce/enterprise-product-options/src/transformers/productOptionsMap'
import { SwatchShape } from '@thg-commerce/gravity-elements/Swatch/types'

import { useProductPage, useReviewsPage } from '../../context'

import {
  AuroraProductOptions,
  ProductOptionSelector,
} from './ProductOptionSelector'

export interface ProductOptions {
  productOptions: {
    options: Option[]
    variants: TransformerVariant[]
    defaultVariant: TransformerVariant
  }
}
export interface ProductDefaultAndVariants {
  productDefaultAndVariants: {
    url: string
    options: Option[]
    variants: TransformerVariant[]
    defaultVariant: TransformerVariant
  }
}

type ProductData = {
  image: string
  price: {
    displayValue: string
    amount: string
  }
  id?: string
  title: any
  sku?: number
  hasFromPrice?: boolean
}

export interface BasketItem {
  product: {
    sku: string
  }
  quantity: number
}

export interface BasketData {
  items: BasketItem[]
}

const showSubsTab = (
  selectedVariant: TransformerVariant,
  pdpDefaultSubscribeAndSave?: Boolean,
): number => {
  if (
    pdpDefaultSubscribeAndSave &&
    selectedVariant?.subscriptionData?.subscriptionContracts?.length > 0
  ) {
    return 1
  }

  if (
    selectedVariant?.subscriptionPaymentType ===
      SubscriptionPaymentType.ON_DISPATCH &&
    selectedVariant.subscriptionData?.subscribeOptions?.upfront.length
  ) {
    return 1
  }

  return 0
}

export const ProductOptionSelectorRenderer = <
  Variant extends TransformerVariant
>(props: {
  variantChangedCallback: (variant: Variant) => void
  product?: ProductPageData
  options: VariantChoices
  selectedVariant: Variant
  masterProductSku: number
  sortedOptionKeys: string[]
  queryFullVariantData: boolean
  variants: Variant[]
  forceDropDowns?: boolean
  swatchShape?: SwatchShape
  showSwatches?: boolean
  frequentlyBoughtTogetherProducts?: (data: ProductData) => void
  defaultVariant?: Variant
  notifyWhenInStock?: boolean
  enableQuantity?: boolean
  labelCustomComponent?: React.ReactNode
  setSelectedOptionsCallback?: (selectedOptions: SelectedOptions) => void
  selectedOptions?: SelectedOptions
  choiceMap?: ChoiceMap
  enableMobilePDPStickyAddToBasket?: boolean
  isFirstProductRecommendation?: boolean
  enablePriceOnMobilePDPStickyAddToBasket?: boolean
  defaultProductOptions?: AuroraOption[] | null
  initialSelectedOptions?: SelectedOptions | null
  linkedOn?: string
  disableColorDropdown?: boolean
  enableSubscribeAndSaveInlinePricing?: boolean
  storeStockActive?: boolean
  setStoreStockActive?: (value: boolean) => void
}) => {
  const productPageContext = useProductPage()
  const reviewPageContext = useReviewsPage()
  const { pdpDefaultSubscribeAndSave } = useSiteConfig()

  const [
    activeTabIndexProductOption,
    setActiveTabIndexProductOption,
  ] = React.useState(
    showSubsTab(props.selectedVariant, pdpDefaultSubscribeAndSave),
  )

  React.useEffect(() => {
    if (
      productPageContext.activeTabIndex !== activeTabIndexProductOption &&
      productPageContext.setActiveTabIndex
    ) {
      productPageContext.setActiveTabIndex(activeTabIndexProductOption)
    }
  }, [activeTabIndexProductOption])

  React.useEffect(() => {
    const shouldShowSubTab = Boolean(
      pdpDefaultSubscribeAndSave &&
        props.selectedVariant?.subscriptionData?.subscriptionContracts?.length >
          0,
    )
    setActiveTabIndexProductOption(shouldShowSubTab ? 1 : 0)
  }, [props.selectedVariant?.subscriptionData?.subscriptionContracts])

  const preselectVariant =
    productPageContext.preselectVariant || reviewPageContext.preselectVariant

  const i18nText = {
    productOptions: {
      swatch: {
        unavailableText: i18n(
          'product.productoptions.swatch.tooltip.unavailable.text',
        ),
        closeButtonText: i18n(
          'product.productoptions.swatch.tooltip.close.text',
        ),
      },
    },
    productOptionsSelector: {
      quantitySelector: {
        increaseButtonLabel: i18n('general.quantity.increase'),
        decreaseButtonLabel: i18n('general.quantity.decrease'),
        quantityLabel: i18n('general.quantity.text'),
      },
      addToBasketButton: {
        addToBasketText: i18n('product.optionselector.addtobasket.text'),
        outOfStockText: i18n('product.optionselector.outofstock.text'),
        preorderNowText: i18n('listitem.button.preordernow.text'),
        personaliseProductText: i18n(
          'product.optionselector.personalisedproduct.text',
        ),
      },
      clickAndCollect: {
        clickAndCollectButtonText: i18n(
          'product.optionselector.clickandcollect.button.text',
        ),
        clickAndCollectLinkText: i18n(
          'product.optionselector.clickandcollect.link.text',
        ),
        clickAndCollectCheckStoreStockText: i18n(
          'product.optionselector.clickandcollect.button.checkstorestock.text',
        ),
      },
    },
  }

  const renderProps = {
    i18nText: i18nText.productOptionsSelector,
    forceDropDowns: props.forceDropDowns,
    enableQuantity:
      props.enableQuantity ?? !props.frequentlyBoughtTogetherProducts,
    swatchShape: props.swatchShape,
    showSwatches: props.showSwatches,
    options: props.options,
    labelCustomComponent: props.labelCustomComponent,
    enableMobilePDPStickyAddToBasket: props.enableMobilePDPStickyAddToBasket,
    isFirstProductRecommendation: props.isFirstProductRecommendation,
    enablePriceOnMobilePDPStickyAddToBasket:
      props.enablePriceOnMobilePDPStickyAddToBasket,
  }

  return props.initialSelectedOptions ? (
    <AuroraProductOptions
      i18nText={i18nText.productOptions}
      choiceMap={props.choiceMap}
      selectedOptions={props.initialSelectedOptions}
      setSelectedOptionsCallback={props.setSelectedOptionsCallback}
      forceDropDowns={props.forceDropDowns}
      enableQuantity={
        props.enableQuantity ?? !props.frequentlyBoughtTogetherProducts
      }
      masterProductSku={props.masterProductSku}
      swatchShape={props.swatchShape}
      showSwatches={props.showSwatches}
      labelCustomComponent={props.labelCustomComponent}
      enableMobilePDPStickyAddToBasket={props.enableMobilePDPStickyAddToBasket}
      isFirstProductRecommendation={props.isFirstProductRecommendation}
      enablePriceOnMobilePDPStickyAddToBasket={
        props.enablePriceOnMobilePDPStickyAddToBasket
      }
      // TODO fix type REBUILD-8692
      initialOptions={
        props.defaultProductOptions ? props.defaultProductOptions : []
      }
      variantChangedCallback={props.variantChangedCallback}
      notifyWhenInStock={props.notifyWhenInStock}
      sortedOptionKeys={props.sortedOptionKeys}
      selectedVariant={props.selectedVariant}
      queryFullVariantData={props.queryFullVariantData}
      product={props.product}
      preselectVariant={preselectVariant}
      linkedOn={props.linkedOn}
      parentSku={props.product?.sku}
      disableColorDropdown={props.disableColorDropdown}
      enableSubscribeAndSaveInlinePricing={
        props.enableSubscribeAndSaveInlinePricing
      }
      setActiveTabIndex={setActiveTabIndexProductOption}
      activeTabIndex={activeTabIndexProductOption}
      setStoreStockActive={props.setStoreStockActive}
      storeStockActive={props.storeStockActive}
    />
  ) : (
    <ProductOptionSelector
      {...renderProps}
      choiceMap={props.choiceMap}
      selectedOptions={props.selectedOptions}
      setSelectedOptionsCallback={props.setSelectedOptionsCallback}
      variantChangedCallback={props.variantChangedCallback}
      selectedVariant={props.selectedVariant}
      defaultVariant={props.defaultVariant}
      variants={props.variants}
      notifyWhenInStock={props.notifyWhenInStock}
      parentSku={props.product?.sku}
      disableColorDropdown={props.disableColorDropdown}
      enableSubscribeAndSaveInlinePricing={
        props.enableSubscribeAndSaveInlinePricing
      }
      setActiveTabIndex={setActiveTabIndexProductOption}
      activeTabIndex={activeTabIndexProductOption}
    />
  )
}
